/* eslint-disable import/order */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import './input-wrapper.sass';
import { withTemplate } from '../../hoc-components/hoc-context/with-template';
import { bindActionCreators, compose } from 'redux';
import { connect, shallowEqual, useSelector } from 'react-redux';
import { withAdr } from '../../hoc-components/with-adr';
import {
  cashierValidationFailedEvent,
  cashierValidationSuccessEvent,
} from '../../../utils/custom-cashier-events/cashier-validation-event';
import { debounce } from '../../../utils/helper-functions/debounce';
import ErrorIcon from '../../../@pano-project/assets/icons/error-icon';

const InputWrapper = (props) => {
  let {
    children: input,
    error,
    title,
    icon = false,
    initShowError = false,
    alwaysShowError = false,
    fieldType,
    className = '',
    template,
    name,
    focus,
    layout,
    show_all_errors,
    option_id,
    option_type,
    s,
    hint,
    input_field_style,
  } = props;

  const payData = useSelector((state) => state.payData, shallowEqual);
  const [changed, setChanged] = useState(initShowError);
  const ref = useRef();
  const blurTimeoutRef = useRef(null);
  const debounceCashierValidationEvent = useCallback(
    debounce((name, inputValue, error) => {
      if (error) {
        cashierValidationFailedEvent({ field: name, value: inputValue, message: error });
      } else {
        cashierValidationSuccessEvent({
          field: name,
          value: inputValue,
          message: 'Validation Success',
        });
      }
    }, 700),
    [],
  );

  useEffect(() => {
    if (focus === name && !changed) {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }
      setChanged(true);
    } else if (focus !== name && changed) {
      blurTimeoutRef.current = setTimeout(() => {
        if (focus !== name) {
          setChanged(true);
        }
      }, 50);
    }
  }, [focus]);

  const lastFocusRef = useRef(focus);
  useEffect(() => {
    // PRX-21746: only a change of the focus state to this field moves the
    // caret. A field drawn again while it already holds it (amount-first
    // step 1 after pending withdrawals) leaves the focus where it is.
    const alreadyHeldFocus = lastFocusRef.current === name;
    lastFocusRef.current = focus;
    if (layout !== 'amount-first' || focus !== name || alreadyHeldFocus || !ref.current) return;

    const inputElement = ref.current.querySelector('input');
    if (inputElement && document.activeElement !== inputElement) {
      inputElement.focus();
    }
  }, [focus, name, layout]);

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!changed) return;
    debounceCashierValidationEvent(name, payData[name], error);
  }, [error, changed, payData[name]]);

  useEffect(() => {
    setChanged(false);
  }, [option_id, option_type]);

  if (typeof error === 'array') error = String(error);

  if (!input) {
    const errorText = 'InputWrapper must have an input as children props';

    console.error(errorText);
  }

  // alwaysShowError only reveals the error; unlike initShowError it does not mark the field as
  // changed, so the public host validation events are not armed from first paint.
  let hasError = (changed || show_all_errors || initShowError || alwaysShowError) && error;
  if (focus === name) hasError = false;

  const forceErrorShow = (fieldType === 'bic' && hasError) || (fieldType === 'iban' && hasError);

  if (template.error === 'top') {
    return (
      <div
        ref={ref}
        className={`${s('input-item')} ${className} ${focus === name ? s('focus') : ''} ${icon ? s('has-icon') : ''} ${hasError ? s('has-error') : ''}`}
        data-title={title || ''}
        {...(layout === 'amount-first' && { 'data-error-visible': !!hasError })}
      >
        <div className={`${s('title')}`}>{hasError ? error : title}</div>
        <div className={`${s('input-wrap')}`}>
          {input}
          {icon ? <div className={`${s('input-icon')}`}>{icon}</div> : ''}
        </div>
        {hint && <div className="hint">{hint}</div>}
      </div>
    );
  }

  const titleNode = (
    <div className={`${s('title')} ${title ? '' : s('title-none')}`} data-title={title || ''}>
      {title}
    </div>
  );

  return (
    <div
      ref={ref}
      className={`${s('input-item')} ${className} ${focus === name ? s('focus') : ''} ${hasError ? s('has-error') : ''}`}
      {...(layout === 'amount-first' && { 'data-error-visible': !!hasError })}
    >
      {(input_field_style === 'top_label' || input_field_style === undefined) && titleNode}
      <div className={`${s('input-wrap')}`}>
        {input_field_style === 'floating_label' && titleNode}
        {input}
        {icon ? <div className={`${s('input-icon')}`}>{icon}</div> : ''}
      </div>
      <div
        className={`${s('error')} ${hasError ? s('show') : s('hide')} ${forceErrorShow ? 'force-show' : ''} ${layout === 'amount-first' && hasError ? 'amount-first-error' : ''}`}
      >
        {error}
      </div>
      {hint && <div className="hint">{hint}</div>}
    </div>
  );
};

const mapStateToProps = ({
  errors: { show_all_errors },
  currentGateway: { option_id, option_type },
  focus,
  initTheme,
}) => {
  const layout = initTheme?.theme_editor_settings?.customization_settings?.layout;
  const input_field_style =
    initTheme?.theme_editor_settings?.customization_settings?.input_field_style;

  return { show_all_errors, option_id, option_type, focus, layout, input_field_style };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators({}, dispatch);
};

export default compose(
  withTemplate,
  withAdr,
  connect(mapStateToProps, mapDispatchToProps),
)(InputWrapper);
